import {DomainStore} from 'shared/store';
import { makeAutoObservable } from 'mobx';
import {endpoints, types} from 'main/tools';
import PublicInterviewInvite from 'main/stores/ats/PublicInterviewInvite';
import {dateStringInZone, formatTimeInZone} from 'shared/tools/dates';
import _ from 'lodash';
import moment from 'moment';
import {errorAlert, successAlert} from 'shared/tools';
import {t} from 'shared/core';

class ScheduleInterviewState {
  store = new DomainStore();
  history;

  interviewInvite;
  selectedDate;
  selectedSlot;

  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  constructor() {
    makeAutoObservable(this, {
      store: false,
      history: false
    });
  }

  receiveProps({history}) {
    this.history = history;
  }

  async load() {
    const token = new URLSearchParams(location.search).get('token');

    await this.store._compose([
      endpoints.INTERVIEW_INVITE.PUBLIC.with(token)
    ]);
    this.interviewInvite = new PublicInterviewInvite(this.store._getSingle(types.INTERVIEW_INVITE));
  }

  async loadPreview({interviewScheduleId, startDate, endDate}) {
    await this.store._compose([
      endpoints.INTERVIEW_INVITE.PREVIEW.with(interviewScheduleId, startDate, endDate)
    ]);
    this.interviewInvite = new PublicInterviewInvite(this.store._getSingle(types.INTERVIEW_INVITE));
  }

  async scheduleInterview() {
    const {model, errors} = await this.store.post(
      this.interviewInvite.link('schedule'),
      types.INTERVIEW_INVITE,
      {startTime: this.selectedSlot.startTime}
    );

    if (!_.isEmpty(errors)) {
      errorAlert(errors.base);
      return;
    }

    this.interviewInvite.update(model);
    this.history.push({pathname: '/complete', search: this.history.location.search});
  }

  async cancelInterview() {
    const {model, errors} = await this.store.post(
      this.interviewInvite.link('cancel'),
      types.INTERVIEW_INVITE
    );

    if (!_.isEmpty(errors)) {
      errorAlert(errors.base);
      return;
    }

    this.interviewInvite.update(model);
    successAlert(t('recruiting.schedule_interview.Your interview has been cancelled'));
  }

  setTimeZone(timeZone) {
    this.timeZone = timeZone;
    this.selectedSlot = null;
    if (!this.isDateBookable(this.selectedDate)) {
      this.selectedDate = null;
    }
  }

  setSelectedDate(date) {
    this.selectedDate = date;
    this.selectedSlot = null;
  }

  setSelectedSlot(slot) {
    if (this.interviewInvite.booked) return;

    this.selectedSlot = slot;
  }

  isDateBookable(date) {
    return this.slotsForDate(date).length > 0;
  }

  slotsForDate(date) {
    const targetDate = moment(date).format('YYYY-MM-DD');
    return _.get(this.bookableSlotsInTimeZone, targetDate, []);
  }

  get bookableSlotsInTimeZone() {
    const slotsInTimeZone = this.interviewInvite.bookableSlots.map((slot) => ({
      ...slot,
      day: dateStringInZone(slot.startTime, this.timeZone),
      startTimeInZone: formatTimeInZone(slot.startTime, this.timeZone)
    }));

    return _.groupBy(slotsInTimeZone, 'day');
  }

  get firstAvailableDate() {
    const days = Object.keys(this.bookableSlotsInTimeZone);

    if (days.length === 0) return null;

    return moment(days[0]).toDate();
  }
}

export default ScheduleInterviewState;
